// Pure helpers behind the wheel action cards (C5): which proposal types confirm into labiq,
// the deterministic batch identity, and the blast-radius line "Add all" states before writing.
import type { XoeProposedAction } from "../../types/xoe/XoeTypes";

// labiq's WheelActionCatalogue::WRITE_DEFS — the five action_type strings are FINAL there.
export const WHEEL_ACTION_TYPES = new Set(["create_wheel", "create_wheel_trait", "add_wheel_snps", "remove_wheel_snp", "add_wheel_gene"]);

export const isWheelActionType = (actionType: string): boolean => WHEEL_ACTION_TYPES.has(actionType);

export interface WheelSnpEntry {
	rs: string;
	risk_allele?: string;
	subcategory?: string | null;
	gene?: string;
	bene_allele?: string;
	notes?: string;
	rank?: number;
}

/** The snps[] of an add_wheel_snps proposal, entries without an rs string dropped. */
export const wheelSnpEntries = (params: Record<string, unknown>): WheelSnpEntry[] => {
	const snps = (params as { snps?: unknown }).snps;
	if (!Array.isArray(snps)) return [];
	return snps.filter((entry): entry is WheelSnpEntry => !!entry && typeof entry === "object" && typeof (entry as { rs?: unknown }).rs === "string");
};

/**
 * Deterministic client-side batch identity: message id + FNV-1a over the SORTED action ids.
 * A double-click, a retry or a remounted card re-derives the same id, so the server's
 * already_confirmed short-circuit answers instead of a duplicate batch being written.
 */
export const deriveWheelBatchId = (messageId: number, actionIds: string[]): string => {
	const key = [...actionIds].sort().join("\n");
	let hash = 0x811c9dc5;
	for (let index = 0; index < key.length; index += 1) {
		hash ^= key.charCodeAt(index);
		hash = Math.imul(hash, 0x01000193) >>> 0;
	}
	return `wheel-${messageId}-${hash.toString(16).padStart(8, "0")}`;
};

const plural = (count: number, noun: string): string => `${count} ${noun}${count === 1 ? "" : "s"}`;

/** The blast radius of a batch, computed from the ITEMS — stated before anything is written. */
export const describeWheelBlastRadius = (actions: XoeProposedAction[]): string => {
	let wheels = 0;
	let traitsCreated = 0;
	let snps = 0;
	let removes = 0;
	let genes = 0;
	let unassigned = 0;
	const traitBuckets = new Set<string>();

	actions.forEach((action) => {
		switch (action.action_type) {
			case "create_wheel":
				wheels += 1;
				break;
			case "create_wheel_trait":
				traitsCreated += 1;
				break;
			case "add_wheel_snps":
				wheelSnpEntries(action.params).forEach((entry) => {
					snps += 1;
					const trait = typeof entry.subcategory === "string" && entry.subcategory.trim() !== "" ? entry.subcategory : null;
					if (trait) traitBuckets.add(trait);
					else unassigned += 1;
				});
				break;
			case "remove_wheel_snp":
				removes += 1;
				break;
			case "add_wheel_gene":
				genes += 1;
				break;
			default:
				break;
		}
	});

	const parts: string[] = [];
	if (wheels > 0) parts.push(wheels === 1 ? "creates a wheel" : `creates ${wheels} wheels`);
	if (traitsCreated > 0) parts.push(`creates ${plural(traitsCreated, "trait")}`);
	if (snps > 0) {
		if (traitBuckets.size > 0) {
			const suffix = unassigned > 0 ? ` (+${unassigned} unassigned)` : "";
			parts.push(`adds ${plural(snps, "SNP")} under ${plural(traitBuckets.size, "trait")}${suffix}`);
		} else {
			parts.push(`adds ${plural(snps, "SNP")}, unassigned`);
		}
	}
	if (genes > 0) parts.push(`adds ${plural(genes, "gene")}`);
	if (removes > 0) parts.push(`removes ${plural(removes, "SNP")}`);
	return parts.join(" · ");
};
